import { BoxGeometry, Mesh, MeshBasicMaterial } from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls";
import { CollisionFacet, UpdateFacetOnModelMessage, BroadcastToPeerMessage, DisablePeerGizmoMessage, convertToNumber3, convertToNumber4, LocalTransformFacet, SendToServerMessage, RemoveFacetFromModelMessage, GizmoLockFacet } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { MoveGrabbableMessage, NoModelClickedMessage, PickupGrabbableMessage, ReleaseGrabbableMessage } from "@messages/interaction/InteractionMessages";
import { RequestCamRotationActiveMessage } from "@messages/movement/MovementMessages";
import { UpdateCollisionShapeSelectableStateMessage } from "@messages/selection/CollisionShapeMessages";
import { EnableModelSelectionMessage } from "@messages/selection/SelectionStrategyMessages";
import { camera } from "@modules/rendering/camera";
import { renderer } from "@modules/rendering/RendererGlobals";
import { mainScene } from "@modules/rendering/mainScene";
import { modelPool, computeLocalMatrixFromWorldMatrix, computeLocalMatrixFromWorldTransformComponents } from "@modules/model";
import { coreUserService } from "@modules/users";
const INTERACTION_FRAME_TIME = 1000 / 16.67; // roughly 60 fps
/**
 * The model controller encapsulates the behavior of the gizmo. It allows to show and hide the gizmo, allows to change
 * the transform operation to translate, rotate or scale as well as changing the coordinate space to either local or
 * global and can reset the scale. It uses a dummy target that is transformed when interacting with the gizmo, its
 * transformation is then sent to the currently selected model. It contains some constraints on how big or small the
 * selected model can be scaled.
 */
export class GizmoModelController {
    constructor(connection) {
        this.modelUid = -1;
        this.connection = connection;
        this.minScale = 0.1;
        this.maxScale = 10;
        this.controls = new TransformControls(camera.perspectiveCam, renderer.domElement);
        this.helper = this.controls.getHelper();
        mainScene.add(this.helper);
        this.helper.visible = false;
        this.dummyTarget = new Mesh(new BoxGeometry(1, 1, 1), new MeshBasicMaterial());
        this.controls.attach(this.dummyTarget);
        mainScene.add(this.dummyTarget);
        this.dummyTarget.visible = false;
        this.dummyTarget.updateWorldMatrix(false, false);
        this.rotationCorrectionTransform = new Matrix4();
        this.ignoreRotationCorrection = true;
        this.worldTransform = new Matrix4();
        this.grabbed = false;
        this.locallySuppressedModelUid = -1;
        // Set callbacks last
        this.controls.addEventListener("mouseDown", () => {
            this.startInteraction();
        });
        this.controls.addEventListener("mouseUp", () => {
            this.endInteraction();
        });
        this.interactionInterval = undefined;
    }
    /**
     * Shows a previously hidden gizmo
     */
    show(modelUid) {
        // Restore current model state. normally this shouldn't happen
        if (this.modelUid !== -1) {
            this.helper.visible = false;
            this.restoreModelState();
        }
        if (!this.tryAcquireLock(modelUid)) {
            this.modelUid = -1;
            return;
        }
        this.modelUid = modelUid;
        // Set initial state for new model
        this.setCoordinateSpace("world");
        this.setTransformMode("translate");
        const messages = [];
        this.disableSelectionForPeers(this.modelUid, messages);
        this.connection.sendMultiple(messages);
        this.setLocalSelectionSuppressed(this.modelUid, true);
        this.helper.visible = true;
    }
    /**
     * Hides the gizmo
     */
    hide() {
        this.helper.visible = false;
        // Restore current model state
        this.restoreModelState(true);
        this.modelUid = -1;
    }
    /**
     * Updates the gizmo. This is called if the user switches from one selected model to another model.
     */
    update(modelUid) {
        // Hide gizmo while transition
        this.helper.visible = false;
        // Restore current model state
        this.restoreModelState();
        this.modelUid = -1;
        // Update state for new model
        this.show(modelUid);
    }
    /// Attribute getters ///
    getModelUid() {
        return this.modelUid;
    }
    getControls() {
        return this.controls;
    }
    getDummyTarget() {
        return this.dummyTarget;
    }
    /**
     * Get a copy of the current world transform of the gizmo
     */
    getWorldTransform() {
        const transform = this.dummyTarget.matrixWorld.clone();
        if (!this.ignoreRotationCorrection)
            transform.copy(this.combineTransformations(transform, this.rotationCorrectionTransform));
        return transform;
    }
    setMinScale(minScale) {
        this.minScale = minScale;
    }
    setMaxScale(maxScale) {
        this.maxScale = maxScale;
    }
    /// Interaction ///
    /**
     * Called when the user grabs the gizmo and performs an interaction. The call to this function is silently ignored
     * if no model is selected.
     */
    startInteraction() {
        if (this.modelUid === -1) {
            this.controls.dragging = false;
            return;
        }
        this.grabbed = true;
        const messages = [];
        this.startGrabbing(this.modelUid, messages);
        this.lockCameraRotation(messages);
        this.connection.sendMultiple(messages);
        // For now, we only allow to scale uniformly because non-uniform scaling causes
        // issues in combination with rotations
        if (this.controls.mode === "scale")
            this.controls.axis = "XYZ";
        // Update world transform in case endInteraction is called without
        // a single call to updateInteraction
        const transform = this.getWorldTransform();
        this.worldTransform.copy(transform);
        this.startContinuouslyUpdatingTheModelTransform();
    }
    /**
     * updateInteraction updates the transform of the active model. Nothing will be done if no model is selected. The
     * model will also not be updated while the gizmo is not grabbed. To activate grabbing you have to call
     * startInteraction first or pass force = true to this function
     */
    updateInteraction() {
        if (this.modelUid === -1)
            return;
        // We move the model before the interaction has started. This shouldn't happen normally but seems to happen
        // occasionally. In that case model and gizmo are out of sync, thus we first have to start interaction to keep them
        // in sync
        if (!this.grabbed && this.helper.visible) {
            console.warn("[gizmo] startInteraction inside updateInteraction!");
            this.startInteraction();
        }
        this.updateModelTransform();
        this.broadcastMoveGrabbedObject();
    }
    /**
     * Called to stop the interaction with the gizmo. This action is only performed if the gizmo was previously grabbed
     * and if a model is selected
     */
    endInteraction() {
        if (!this.grabbed)
            return;
        if (this.modelUid === -1)
            return;
        const position = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        this.worldTransform.decompose(position, rotation, scale);
        const messages = [];
        this.endGrabbing(this.modelUid, messages);
        this.updateTransformForPeersAndServer(this.modelUid, position, rotation, scale, messages);
        this.unlockCameraRotation(messages);
        this.connection.sendMultiple(messages);
        this.grabbed = false;
        this.stopContinuouslyUpdatingTheModelTransform();
        // The end interaction callback can happen before we caught up with the final gizmo position if we move too fast
        // Therefore delay the last model transform a bit
        const currentModelUID = this.modelUid;
        setTimeout(() => {
            if (this.modelUid !== currentModelUID)
                return;
            this.updateModelTransform();
            this.broadcastMoveGrabbedObject();
        }, INTERACTION_FRAME_TIME);
    }
    /// Update transform state ///
    setTransform(position, rotation, scale, isScalable) {
        this.dummyTarget.position.x = position.x;
        this.dummyTarget.position.y = position.y;
        this.dummyTarget.position.z = position.z;
        this.dummyTarget.quaternion.x = rotation.x;
        this.dummyTarget.quaternion.y = rotation.y;
        this.dummyTarget.quaternion.z = rotation.z;
        this.dummyTarget.quaternion.w = rotation.w;
        // ignore scale if locked
        if (isScalable) {
            this.dummyTarget.scale.x = scale.x;
            this.dummyTarget.scale.y = scale.y;
            this.dummyTarget.scale.z = scale.z;
        }
        else {
            this.dummyTarget.scale.x = 1;
            this.dummyTarget.scale.y = 1;
            this.dummyTarget.scale.z = 1;
        }
    }
    setTransformMode(mode, update = false) {
        this.controls.mode = mode;
        if (update)
            this.handleRotationOffset();
    }
    setCoordinateSpace(coordinateSpace, update = false) {
        this.controls.space = coordinateSpace;
        if (update)
            this.handleRotationOffset();
    }
    resetScale() {
        const isWorldScale = this.controls.mode === "scale" && this.controls.space === "world";
        // Reset scaling factor of the correction transform
        if (isWorldScale) {
            const position = new Vector3();
            const rotation = new Quaternion();
            this.rotationCorrectionTransform.decompose(position, rotation, new Vector3());
            this.rotationCorrectionTransform.compose(position, rotation, new Vector3(1, 1, 1));
        }
        this.dummyTarget.scale.set(1, 1, 1);
        this.dummyTarget.updateMatrixWorld(true);
        this.updateModelTransform();
        // Here we cannot broadcast MoveGrabbedObject because this only works when the user interacted with any axis
        // of this gizmo. For reset scale we ony click the respective button but don't interact with the gizmo directly.
        const model = modelPool.getModel(this.modelUid);
        if (model === undefined)
            return;
        const localTransform = computeLocalMatrixFromWorldMatrix(model, this.worldTransform);
        model.setTransform(localTransform, { server: true });
    }
    /// Handling global scaling ///
    handleRotationOffset() {
        const isWorldScale = this.controls.mode === "scale" && this.controls.space === "world";
        const transform = this.dummyTarget.matrixWorld.clone();
        this.controls.detach();
        // We switch from scaling in world coordinates to another mode. in that case we apply
        // the saved correction transform so it isn't lost. then we reset the correction
        // transformation
        if (!this.ignoreRotationCorrection && !isWorldScale) {
            this.applyWorldScaleTransform(transform);
            this.rotationCorrectionTransform.identity();
            this.ignoreRotationCorrection = true;
        }
        // We switch to scaling in world coordinates. here we have to get rid of the current
        // rotation because it affects the orientation of the transform gizmo. Since we want
        // to keep the rotation, we have to save it in a correction transformation that we
        // will use before sending it to the selected model, peers or the server.
        else if (this.ignoreRotationCorrection && isWorldScale) {
            // Set rotation to world axes to simulate scaling in global coordinates. We also
            // reset scaling as we already save it in the correction transformation
            this.dummyTarget.quaternion.copy(new Quaternion());
            this.dummyTarget.scale.copy(new Vector3(1, 1, 1));
            // Save transform without translation offset
            const position = new Vector3();
            const rotation = new Quaternion();
            const scale = new Vector3();
            transform.decompose(position, rotation, scale);
            transform.compose(new Vector3(), rotation, scale);
            this.rotationCorrectionTransform.copy(transform);
            this.ignoreRotationCorrection = false;
        }
        this.dummyTarget.updateMatrixWorld(true);
        this.controls.attach(this.dummyTarget);
    }
    applyWorldScaleTransform(transform) {
        const newTransform = this.combineTransformations(transform, this.rotationCorrectionTransform);
        // Apply transform so it isn't lost
        const position = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        newTransform.decompose(position, rotation, scale);
        this.dummyTarget.position.copy(position);
        this.dummyTarget.quaternion.copy(rotation);
        this.dummyTarget.scale.copy(scale);
    }
    combineTransformations(relativeTransform, worldTransform) {
        return relativeTransform.clone().multiply(worldTransform);
    }
    /// Interaction helper ///
    updateModelTransform() {
        if (this.modelUid === -1)
            return;
        this.restrictScale();
        const transform = this.getWorldTransform();
        this.worldTransform.copy(transform);
    }
    restoreModelState(deselect = false) {
        // Apply world scale transform and reset
        this.applyWorldScaleTransform(this.dummyTarget.matrixWorld);
        this.rotationCorrectionTransform.identity();
        this.ignoreRotationCorrection = true;
        // If we have an active interaction stop it
        if (this.grabbed)
            this.endInteraction();
        this.releaseLock(this.modelUid);
        this.setLocalSelectionSuppressed(this.modelUid, false);
        const messages = [];
        if (deselect)
            this.deselectModel(messages);
        this.connection.sendMultiple(messages);
    }
    startContinuouslyUpdatingTheModelTransform() {
        // Update at roughly 60fps
        this.interactionInterval = setInterval(() => {
            this.updateInteraction();
        }, INTERACTION_FRAME_TIME);
    }
    stopContinuouslyUpdatingTheModelTransform() {
        if (this.interactionInterval === undefined)
            return;
        clearInterval(this.interactionInterval);
    }
    /// Messaging ///
    disableSelectionForPeers(modelUid, messages) {
        messages.push(new BroadcastToPeerMessage([new DisablePeerGizmoMessage(modelUid)]));
    }
    deselectModel(messages) {
        // we inform the interaction module that the model was deselected
        messages.push(new NoModelClickedMessage());
    }
    updateTransformForPeersAndServer(modelUid, position, rotation, scale, messages) {
        const updatePositionMessage = new MoveGrabbableMessage(modelUid, convertToNumber3(position), convertToNumber4(rotation), convertToNumber3(scale), false);
        messages.push(updatePositionMessage);
        messages.push(new BroadcastToPeerMessage([updatePositionMessage]));
        const model = modelPool.getModel(modelUid);
        if (model === undefined)
            return;
        const localTransform = computeLocalMatrixFromWorldTransformComponents(model, position, rotation, scale);
        const localPosition = new Vector3();
        const localRotation = new Quaternion();
        const localScale = new Vector3();
        localTransform.decompose(localPosition, localRotation, localScale);
        const localTransformFacet = new LocalTransformFacet(convertToNumber3(localPosition), convertToNumber4(localRotation), convertToNumber3(localScale));
        messages.push(new SendToServerMessage([new UpdateFacetOnModelMessage(modelUid, localTransformFacet)]));
    }
    startGrabbing(modelUid, messages) {
        messages.push(new PickupGrabbableMessage(modelUid));
    }
    moveGrabbedObject(position, rotation, scale, messages) {
        messages.push(new MoveGrabbableMessage(this.modelUid, convertToNumber3(position), convertToNumber4(rotation), convertToNumber3(scale), false));
    }
    endGrabbing(modelUid, messages) {
        messages.push(new ReleaseGrabbableMessage(modelUid));
    }
    lockCameraRotation(messages) {
        messages.push(new RequestCamRotationActiveMessage(false));
    }
    unlockCameraRotation(messages) {
        messages.push(new RequestCamRotationActiveMessage(true));
    }
    updateSelectableStateForCurrentLock(uid) {
        var _a;
        const isCurrentModel = uid === this.modelUid;
        const lockFacet = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(GizmoLockFacet);
        const isLockedByOther = lockFacet !== undefined && lockFacet.clientIdx !== this.getLocalClientIdx();
        if (isCurrentModel && isLockedByOther) {
            this.helper.visible = false;
            this.restoreModelState(true);
            this.modelUid = -1;
            return;
        }
        this.refreshLocalSelectableState(uid);
    }
    /// Helper methods ///
    restrictScale() {
        const targetObject = this.controls.object;
        if (targetObject === undefined) {
            return;
        }
        // In case we are in world scale mode we need to take into account the scale of the correction term as well
        const isWorldScale = this.controls.mode === "scale" && this.controls.space === "world";
        if (isWorldScale) {
            // In principle, we could have different scalings. As it would be difficult to take into account the rotation,
            // instead we only care about the biggest scaling factor and use this value to adjust the min and max values
            // This should be fine because world scaling is done in all dimensions at the same time
            const scale = new Vector3();
            this.rotationCorrectionTransform.decompose(new Vector3(), new Quaternion(), scale);
            const maxScaleComponent = Math.max(scale.x, Math.max(scale.y, scale.z));
            const correctedMin = this.minScale / maxScaleComponent;
            const correctedMax = this.maxScale / maxScaleComponent;
            targetObject.scale.clampScalar(correctedMin, correctedMax);
        }
        else {
            targetObject.scale.clampScalar(this.minScale, this.maxScale);
        }
        targetObject.updateMatrixWorld(true);
    }
    broadcastMoveGrabbedObject() {
        const position = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        this.worldTransform.decompose(position, rotation, scale);
        const messages = [];
        this.moveGrabbedObject(position, rotation, scale, messages);
        this.connection.send(messages[0]);
    }
    tryAcquireLock(modelUid) {
        const model = modelPool.getModel(modelUid);
        if (model === undefined)
            return false;
        const existingLock = model.getFacet(GizmoLockFacet);
        const localClientIdx = this.getLocalClientIdx();
        if (existingLock !== undefined && existingLock.clientIdx !== localClientIdx) {
            return false;
        }
        this.connection.send(new UpdateFacetOnModelMessage(modelUid, new GizmoLockFacet(localClientIdx)));
        return true;
    }
    releaseLock(modelUid) {
        if (modelUid === -1)
            return;
        const model = modelPool.getModel(modelUid);
        const existingLock = model === null || model === void 0 ? void 0 : model.getFacet(GizmoLockFacet);
        if (existingLock === undefined || existingLock.clientIdx !== this.getLocalClientIdx())
            return;
        this.connection.send(new RemoveFacetFromModelMessage(modelUid, GizmoLockFacet));
    }
    setLocalSelectionSuppressed(modelUid, suppressed) {
        if (modelUid === -1)
            return;
        this.locallySuppressedModelUid = suppressed ? modelUid : -1;
        this.refreshLocalSelectableState(modelUid);
    }
    refreshLocalSelectableState(modelUid) {
        var _a, _b, _c;
        const model = modelPool.getModel(modelUid);
        const baseSelectable = (_b = (_a = model === null || model === void 0 ? void 0 : model.getFacet(CollisionFacet)) === null || _a === void 0 ? void 0 : _a.isSelectable) !== null && _b !== void 0 ? _b : false;
        const lockOwner = (_c = model === null || model === void 0 ? void 0 : model.getFacet(GizmoLockFacet)) === null || _c === void 0 ? void 0 : _c.clientIdx;
        const isLockedByOther = lockOwner !== undefined && lockOwner !== this.getLocalClientIdx();
        const isLocallySuppressed = modelUid === this.locallySuppressedModelUid;
        const selectable = baseSelectable && !isLockedByOther && !isLocallySuppressed;
        this.connection.send(new UpdateCollisionShapeSelectableStateMessage(modelUid, selectable));
        this.connection.send(new EnableModelSelectionMessage(modelUid, selectable));
    }
    getLocalClientIdx() {
        return coreUserService.getLocalUser().clientIdx;
    }
}
